import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Trophy, Search, X, Check, Star, Shield } from 'lucide-react';
import { SHEET_ANIMATION, BACKDROP_ANIMATION } from './sheetAnimationConfig';
/**
 * LeagueSheet - Bottom sheet for league selection
 * Sport-agnostic league picker with tier badges and search
 * 
 * @param {boolean} isOpen - Whether sheet is open
 * @param {Function} onClose - Callback to close sheet
 * @param {Array|string} selectedLeagues - Selected league(s)
 * @param {Function} onLeagueSelect - Callback when leagues are selected
 * @param {Array} leagues - List of available leagues
 * @param {boolean} multiSelect - Allow multiple league selection
 * @param {string} title - Sheet title
 * @param {string} sport - Current sport context for filtering
 */
const LeagueSheet = ({
  isOpen,
  onClose,
  selectedLeagues,
  onLeagueSelect,
  leagues = [],
  multiSelect = false,
  title = 'Select League',
  sport = 'all'
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedItems, setSelectedItems] = useState([]);
  const [expandedCountries, setExpandedCountries] = useState(new Set());

  // Normalize selected leagues to array
  useEffect(() => {
    if (selectedLeagues) {
      const normalized = Array.isArray(selectedLeagues) 
        ? selectedLeagues 
        : [selectedLeagues];
      setSelectedItems(normalized);
    } else {
      setSelectedItems([]);
    }
  }, [selectedLeagues]);

  // Filter leagues based on search and sport
  const filteredLeagues = useMemo(() => {
    let filtered = leagues;
    
    // Filter by sport if specified
    if (sport !== 'all') {
      filtered = filtered.filter(league => 
        league.sport === sport || !league.sport
      );
    }
    
    // Filter by search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(league => 
        league.name.toLowerCase().includes(query) ||
        league.country?.toLowerCase().includes(query) ||
        String(league.id).toLowerCase().includes(query)  // Convert to string for both numeric and string IDs
      );
    }
    
    return filtered;
  }, [leagues, searchQuery, sport]);

  // Group leagues by country and tier
  const groupedLeagues = useMemo(() => {
    const groups = {};
    
    filteredLeagues.forEach(league => {
      const country = league.country || 'International';
      if (!groups[country]) {
        groups[country] = {
          tier1: [],
          tier2: [],
          tier3: [],
          other: []
        };
      }
      
      const tier = league.tier || 'other';
      const tierKey = tier === 1 ? 'tier1' : 
                     tier === 2 ? 'tier2' : 
                     tier === 3 ? 'tier3' : 'other';
      
      groups[country][tierKey].push(league);
    });
    
    // Sort countries with popular ones first
    const popularCountries = ['England', 'Spain', 'Germany', 'Italy', 'France', 'Brazil'];
    const sortedCountries = Object.keys(groups).sort((a, b) => {
      const aIndex = popularCountries.indexOf(a);
      const bIndex = popularCountries.indexOf(b);
      
      if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
      if (aIndex !== -1) return -1;
      if (bIndex !== -1) return 1;
      return a.localeCompare(b);
    });
    
    return sortedCountries.map(country => ({
      country,
      leagues: groups[country]
    }));
  }, [filteredLeagues]);

  const handleLeagueToggle = (leagueId) => {
    const normalizedId = String(leagueId);
    if (multiSelect) {
      const newSelection = selectedItems.includes(normalizedId)
        ? selectedItems.filter(l => l !== normalizedId)
        : [...selectedItems, normalizedId];
      
      setSelectedItems(newSelection);
    } else {
      setSelectedItems([normalizedId]);
      onLeagueSelect(normalizedId);
      onClose();
    }
  };

  const handleApply = () => {
    onLeagueSelect(multiSelect ? selectedItems : selectedItems[0]);
    onClose();
  };

  const toggleCountryExpansion = (country) => {
    const newExpanded = new Set(expandedCountries);
    if (newExpanded.has(country)) {
      newExpanded.delete(country);
    } else {
      newExpanded.add(country);
    }
    setExpandedCountries(newExpanded);
  };

  const isSelected = (leagueId) => selectedItems.includes(String(leagueId));

  const getTierBadge = (tier) => {
    switch(tier) {
      case 1:
        return (
          <div className="flex items-center gap-1 px-2 py-0.5 bg-yellow-500/20 
                       text-yellow-400 rounded-full text-xs font-bold">
            <Star size={10} fill="currentColor" />
            <span>Top</span>
          </div>
        );
      case 2:
        return (
          <div className="flex items-center gap-1 px-2 py-0.5 bg-blue-500/20 
                       text-blue-400 rounded-full text-xs font-bold">
            <Shield size={10} />
            <span>Pro</span>
          </div>
        );
      case 3:
        return (
          <div className="px-2 py-0.5 bg-gray-500/20 text-gray-400 
                       rounded-full text-xs font-bold">
            Div 3
          </div>
        );
      default:
        return null;
    }
  };


  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            {...BACKDROP_ANIMATION}
            onClick={onClose}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50"
          />
          
          {/* Sheet */}
          <motion.div
            {...SHEET_ANIMATION}
            className="fixed bottom-0 left-0 right-0 bg-gray-900 rounded-t-3xl z-50 max-h-[85vh] overflow-hidden"
          >
            {/* Handle bar */}
            <div className="flex justify-center pt-2">
              <div className="w-10 h-1 bg-white/20 rounded-full" />
            </div>
            
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
              <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                <Trophy size={20} className="text-emerald-400" />
                {title}
              </h3>
              <button
                onClick={onClose}
                className="p-2 rounded-full hover:bg-white/10 transition-colors"
              >
                <X size={20} className="text-white/70" />
              </button>
            </div>
            
            {/* Search bar */}
            <div className="px-6 py-4 border-b border-white/10">
              <div className="relative">
                <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/50" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search leagues..."
                  className="w-full pl-10 pr-4 py-3 bg-white/10 border border-white/20 
                           rounded-xl text-white placeholder-white/50 
                           focus:bg-white/15 focus:border-emerald-500/50 
                           focus:outline-none transition-all"
                />
              </div>
              
              {multiSelect && (
                <div className="flex items-center justify-between mt-3">
                  <span className="text-sm text-white/60">
                    {selectedItems.length} selected
                  </span>
                </div>
              )}
            </div>
            
            {/* League list grouped by country */}
            <div className="overflow-y-auto max-h-[50vh]">
              {groupedLeagues.map(({ country, leagues: countryLeagues }) => {
                const hasLeagues = Object.values(countryLeagues).some(tier => tier.length > 0);
                if (!hasLeagues) return null;
                
                const isExpanded = expandedCountries.has(country) || searchQuery;
                const allLeagues = [
                  ...countryLeagues.tier1,
                  ...countryLeagues.tier2,
                  ...countryLeagues.tier3,
                  ...countryLeagues.other
                ];
                
                return (
                  <div key={country} className="border-b border-white/10 last:border-0">
                    {/* Country header */}
                    <button
                      onClick={() => toggleCountryExpansion(country)}
                      className="w-full px-6 py-3 flex items-center justify-between 
                               hover:bg-white/5 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <span className="text-white font-medium">{country}</span>
                        <span className="text-xs text-white/50">
                          ({allLeagues.length} leagues)
                        </span>
                      </div>
                      <svg 
                        className={`w-4 h-4 text-white/50 transition-transform ${
                          isExpanded ? 'rotate-180' : ''
                        }`}
                        fill="none" 
                        stroke="currentColor" 
                        viewBox="0 0 24 24"
                      >
                        <path 
                          strokeLinecap="round" 
                          strokeLinejoin="round" 
                          strokeWidth={2} 
                          d="M19 9l-7 7-7-7" 
                        />
                      </svg>
                    </button>
                    
                    {/* Leagues for this country */}
                    {isExpanded && (
                      <div className="bg-black/20">
                        {/* Tier 1 leagues */}
                        {countryLeagues.tier1.map(league => (
                          <button
                            key={league.id}
                            onClick={() => handleLeagueToggle(league.id)}
                            className={`
                              w-full px-6 py-3 flex items-center justify-between
                              transition-colors hover:bg-white/5
                              ${isSelected(league.id) ? 'bg-emerald-500/10' : ''}
                            `}
                          >
                            <div className="flex items-center gap-3">
                              <div className="text-left">
                                <div className="text-white font-medium flex items-center gap-2">
                                  {league.name}
                                  {getTierBadge(league.tier)}
                                </div>
                                {league.subtitle && (
                                  <div className="text-xs text-white/50">{league.subtitle}</div>
                                )}
                              </div>
                            </div>
                            {isSelected(league.id) && (
                              <Check size={18} className="text-emerald-400" />
                            )}
                          </button>
                        ))}
                        
                        {/* Other tier leagues */}
                        {[...countryLeagues.tier2, ...countryLeagues.tier3, ...countryLeagues.other]
                          .map(league => (
                            <button
                              key={league.id}
                              onClick={() => handleLeagueToggle(league.id)}
                              className={`
                                w-full px-6 py-3 flex items-center justify-between
                                transition-colors hover:bg-white/5
                                ${isSelected(league.id) ? 'bg-emerald-500/10' : ''}
                              `}
                            >
                              <div className="flex items-center gap-3">
                                <div className="text-left">
                                  <div className="text-white/80 font-medium flex items-center gap-2">
                                    {league.name}
                                    {getTierBadge(league.tier)}
                                  </div>
                                  {league.subtitle && (
                                    <div className="text-xs text-white/50">{league.subtitle}</div>
                                  )}
                                </div>
                              </div>
                              {isSelected(league.id) && (
                                <Check size={18} className="text-emerald-400" />
                              )}
                            </button>
                          ))}
                      </div>
                    )}
                  </div>
                );
              })}
              
              {filteredLeagues.length === 0 && (
                <div className="px-6 py-12 text-center">
                  <Trophy size={48} className="mx-auto mb-3 text-white/20" />
                  <p className="text-white/50">No leagues found</p>
                </div>
              )}
            </div>
            
            {/* Apply button for multi-select */}
            {multiSelect && (
              <div className="px-6 py-4 border-t border-white/10">
                <button
                  onClick={handleApply}
                  disabled={selectedItems.length === 0}
                  className="w-full py-3 bg-emerald-500 text-white rounded-xl 
                           font-semibold hover:bg-emerald-600 transition-colors
                           disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Apply Selection
                </button>
              </div>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default LeagueSheet;